import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const CrmAccountDrawer = lazyWithReload(() => import('./CrmAccountDrawer'), 'CrmAccountDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * URL-driven `?accountId=<short>`.
 * API: useCrmAccountDrawer() → { openAccount, closeAccount, version, bumpVersion, openAccountId }
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openAccount: noProviderOpener('account'),
  closeAccount: () => {},
  version: 0,
  bumpVersion: () => {},
  openAccountId: null,
};

function CrmAccountDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get('accountId');
  const [version, setVersion] = useState(0);

  const openAccount = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('accountId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('accountId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeAccount = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('accountId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('accountId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('account', openAccount), [openAccount]);

  return (
    <Ctx.Provider value={{ openAccount, closeAccount, version, bumpVersion, openAccountId: accountId }}>
      {children}
      {accountId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeAccount} />}>
          <CrmAccountDrawer open={!!accountId} accountId={accountId} onClose={closeAccount} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useCrmAccountDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const CrmAccountDrawerProvider = providerIfModuleOn('crm', CrmAccountDrawerProviderOn);
